import type { JsonQuestion, JsonQuestionnaireSection } from '@/components/questionnaires/json-driven/types';
import { isFamilyHistoryMedicalValue, isPcosWorkupReference } from './medicalValueContext';
import type { TriggerRule, TriggerRuleConfig } from './types';

/** Questionnaire ids from GET /questionnaires/:id are Mongo ObjectIds, not slugs like "diabetes". */
function isMongoQuestionnaireId(value: string): boolean {
  return /^[a-f0-9]{24}$/i.test(value.trim());
}

function normalizeTriggerValue(value: string): string {
  return value
    .toLowerCase()
    .replace(/[\u2019']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function isUnsupportedQuestionnaireTriggerValue(value: string): boolean {
  const normalized = normalizeTriggerValue(value);
  return (
    normalized === 'graves' ||
    normalized === 'gestational' ||
    isFamilyHistoryMedicalValue(value) ||
    isPcosWorkupReference(value)
  );
}

function normalizeTriggerMappingEntry(
  question: JsonQuestion,
  optionValue: string,
  target: string,
  order: number
): TriggerRule | null {
  const questionnaireId = target.trim();
  const value = optionValue.trim();
  if (!questionnaireId || !value || !isMongoQuestionnaireId(questionnaireId)) return null;
  if (isUnsupportedQuestionnaireTriggerValue(value)) return null;

  return {
    id: `api-${question._id}-${value}`,
    questionnaireId,
    triggerValues: [value],
    triggerReason: `Selected: ${value}`,
    order,
  };
}

/**
 * Optional merge of API `triggerMapping` when targets are real questionnaire _ids.
 * Slugs (e.g. "diabetes") are ignored — use `defaultTriggerRules` ObjectIds for fetch.
 */
export function mergeTriggerConfigFromSection(
  section: JsonQuestionnaireSection | null | undefined,
  baseConfig: TriggerRuleConfig
): TriggerRuleConfig {
  if (!section?.questions?.length) return baseConfig;

  const apiRules: TriggerRule[] = [];
  let order = 0;

  for (const question of section.questions) {
    if (question.isActive === false) continue;
    const mapping = question.triggerMapping;
    if (!mapping) continue;

    if (Array.isArray(mapping)) {
      for (const item of mapping) {
        if (item.triggerType !== 'questionnaire') continue;
        const rule = normalizeTriggerMappingEntry(question, item.optionValue, item.target, order++);
        if (rule) apiRules.push(rule);
      }
      continue;
    }

    for (const [optionValue, target] of Object.entries(mapping)) {
      const rule = normalizeTriggerMappingEntry(question, optionValue, String(target), order++);
      if (rule) apiRules.push(rule);
    }
  }

  if (apiRules.length === 0) return baseConfig;

  return {
    ...baseConfig,
    rules: [...baseConfig.rules, ...apiRules],
  };
}

export function questionHasApiTriggerMapping(question: JsonQuestion): boolean {
  const mapping = question.triggerMapping;
  if (!mapping) return false;

  if (Array.isArray(mapping)) {
    return mapping.some(
      (item) =>
        item.triggerType === 'questionnaire' &&
        typeof item.target === 'string' &&
        item.target.trim().length > 0
    );
  }

  return Object.values(mapping).some(
    (target) => typeof target === 'string' && target.trim().length > 0
  );
}

/** True when API triggerMapping includes at least one Mongo questionnaire _id target. */
export function questionHasMongoTriggerMapping(question: JsonQuestion): boolean {
  const mapping = question.triggerMapping;
  if (!mapping) return false;

  if (Array.isArray(mapping)) {
    return mapping.some(
      (item) =>
        item.triggerType === 'questionnaire' &&
        typeof item.target === 'string' &&
        isMongoQuestionnaireId(item.target)
    );
  }

  return Object.values(mapping).some(
    (target) => typeof target === 'string' && isMongoQuestionnaireId(target)
  );
}
